import { Link } from "@/lib/router-compat";
import { ArrowRight, BookOpen, Sparkles, Clock } from "lucide-react";
import { defaultNewsHeadlines } from "@/components/sections/NewsAndHappenings";

// Select the SAINT-relevant clinical articles created for the site
const saintArticleSlugs = [
  "/diagnosed-with-depression-at-80",
  "/diagnosed-with-depression-at-70",
  "/diagnosed-with-depression-at-60",
  "/diagnosed-with-depression-at-50",
  "/diagnosed-with-depression-at-40",
  "/diagnosed-with-depression-at-30",
  "/diagnosed-with-depression-at-25",
  "/diagnosed-with-depression-as-a-young-adult",
  "/diagnosed-with-depression-as-a-teenager",
  "/depression-in-the-second-half-of-life",
  "/what-to-have-ready-resource-guide-executive-assistant",
  "/how-to-tell-your-boss-youre-worried",
  "/watching-your-boss-fall-apart",
  "/youve-tried-everything-money-can-buy",
  "/how-to-do-due-diligence-on-a-depression-treatment",
  "/the-executive-who-cant-afford-six-weeks",
  "/ending-a-long-term-relationship-in-your-30s-and-40s",
  "/divorce-and-depression",
  "/when-a-breakup-doesnt-get-better",
  "/your-child-got-into-an-ivy-what-nobody-warned-you-about",
  "/depression-in-women",
  "/depression-in-college",
  "/teen-depression-hard-year-or-real-illness",
  "/is-my-child-depressed-what-parents-should-know",
  "/you-just-started-therapy-at-27-thats-not-late",
  "/how-saint-compares-to-other-depression-treatment-options",
  "/saint-tms-plainly-what-it-is-who-its-for-and-what-happens",
  "/youve-tried-everything-why-is-the-depression-still-here",
  "/high-functioning-depression-when-holding-it-together-is-the-problem",
  "/depressed-and-anxious-at-the-same-time",
  "/it-runs-in-my-family-what-that-means-for-your-depression",
  "/understanding-your-phq-9-and-gad-7-scores",
  "/side-effects-youve-stopped-mentioning",
];

const saintArticles = defaultNewsHeadlines.filter((item) =>
  saintArticleSlugs.includes(item.href),
);

// Branded cover images + accent tints for each article
const articleVisuals: Record<
  string,
  { image: string; badge: string; tint: string }
> = {
  "/you-just-started-therapy-at-27-thats-not-late": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/c2bccf98-9d65-4f47-8aef-1b54ce76f713.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/85 to-primary-deep/55",
  },
  "/how-saint-compares-to-other-depression-treatment-options": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/5d614890-2530-4fbb-8746-c41c74b9b65a.png",
    badge: "Treatment Comparison",
    tint: "from-primary-deep/85 to-primary-deep/55",
  },
  "/saint-tms-plainly-what-it-is-who-its-for-and-what-happens": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/adb8b4ad-e880-4f48-a2d2-9150510d15bc.png",
    badge: "Complete Guide",
    tint: "from-primary-deep/80 to-primary-deep/50",
  },
  "/youve-tried-everything-why-is-the-depression-still-here": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/d9915fe6-db2d-410b-b2bc-21517a40d620.png",
    badge: "Patient Journey",
    tint: "from-primary-deep/85 to-primary-deep/55",
  },
  "/high-functioning-depression-when-holding-it-together-is-the-problem": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/c2bccf98-9d65-4f47-8aef-1b54ce76f713.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/80 to-primary-deep/50",
  },
  "/depressed-and-anxious-at-the-same-time": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/8d07c464-5a0b-44ce-b4ac-377847c9e9f0.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/85 to-primary-deep/55",
  },
  "/it-runs-in-my-family-what-that-means-for-your-depression": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/27046b62-cf92-4390-833e-730db3f71baa.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/80 to-primary-deep/50",
  },
  "/understanding-your-phq-9-and-gad-7-scores": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/daa293da-715e-4d45-a020-90350f3d8640.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/85 to-primary-deep/55",
  },
  "/side-effects-youve-stopped-mentioning": {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/53947393-4de8-4ab3-b3c9-85cb83b821eb.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/80 to-primary-deep/50",
  },
};

export const SaintArticlesSection = () => {
  const [featured, ...rest] = saintArticles;
  const featuredVisual = articleVisuals[featured?.href] || {
    image:
      "https://vibe.filesafe.space/1787327131284421342/assets/c2bccf98-9d65-4f47-8aef-1b54ce76f713.png",
    badge: "Clinical Insights",
    tint: "from-primary-deep/85 to-primary-deep/55",
  };

  return (
    <section
      className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-background via-card to-background border-t border-border"
      id="saint-articles"
    >
      <div className="container-px mx-auto max-w-6xl px-4 sm:px-6">
        {/* Section header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 sm:mb-12">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accent">
              <Sparkles className="h-3.5 w-3.5" />
              <span>SAINT® Clinical Articles &amp; Guides</span>
            </span>
            <h2 className="mt-4 font-headings text-2xl font-extrabold text-primary sm:text-3xl lg:text-4xl tracking-tight">
              Learn What SAINT® Means for You
            </h2>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed">
              Plain-language clinical articles to help you understand
              treatment-resistant depression, how SAINT® works, and what to
              expect — written by our psychiatric team.
            </p>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 text-sm font-bold text-accent hover:text-primary transition-colors whitespace-nowrap self-start md:self-auto shrink-0"
          >
            <BookOpen className="h-4 w-4" />
            <span>Search All Articles</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* Featured article — large eye-catching card with cover image */}
        <Link
          to={featured.href}
          className="group block mb-8 sm:mb-10 rounded-3xl overflow-hidden border border-border bg-card shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium"
        >
          <div className="grid md:grid-cols-2">
            {/* Cover image with title overlay */}
            <div className="relative min-h-[220px] md:min-h-[300px] overflow-hidden">
              <img
                src={featuredVisual.image}
                alt={featured.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div
                className={`absolute inset-0 bg-gradient-to-br ${featuredVisual.tint}`}
              />
              <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-8">
                <span className="inline-flex w-fit items-center rounded-full bg-accent px-3 py-1 text-[0.65rem] font-bold uppercase tracking-wider text-primary-deep shadow-md">
                  {featuredVisual.badge}
                </span>
                <h3 className="mt-3 font-headings text-xl sm:text-2xl font-bold text-white leading-snug drop-shadow-lg">
                  {featured.title}
                </h3>
              </div>
            </div>

            {/* Content side */}
            <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <time className="text-xs font-bold tracking-wider uppercase text-muted-foreground">
                  {featured.date}
                </time>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                A side-by-side comparison of SAINT® against medication, standard
                TMS, and ECT — on the two numbers that matter most: remission
                rate and time to remission.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-accent group-hover:gap-3 transition-all">
                <span>Read the Full Comparison</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>

        {/* Grid of remaining articles with cover images */}
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {rest.map((article, idx) => {
            const visual = articleVisuals[article.href];
            return (
              <Link
                key={idx}
                to={article.href}
                className="group flex flex-col rounded-2xl border border-border bg-card shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium hover:-translate-y-1 overflow-hidden"
              >
                {/* Cover image with title overlay */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={
                      visual?.image ||
                      "https://vibe.filesafe.space/1787327131284421342/assets/c2bccf98-9d65-4f47-8aef-1b54ce76f713.png"
                    }
                    alt={article.title}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div
                    className={`absolute inset-0 bg-gradient-to-t ${visual?.tint ?? "from-primary-deep/85 to-primary-deep/45"}`}
                  />
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <span className="inline-flex w-fit items-center rounded-full bg-accent/90 px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-primary-deep shadow-md">
                      {visual?.badge ?? "Article"}
                    </span>
                    <h3 className="mt-2 font-headings text-sm font-bold text-white leading-snug drop-shadow-lg">
                      {article.title}
                    </h3>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <time className="text-[11px] font-bold tracking-wider uppercase text-muted-foreground">
                      {article.date}
                    </time>
                  </div>
                  <div className="mt-auto pt-3 flex items-center gap-1.5 text-xs font-bold text-accent group-hover:gap-2.5 transition-all">
                    <span>Read Article</span>
                    <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-accent px-8 py-3.5 text-sm font-bold text-accent-foreground shadow-glow hover:opacity-90 transition-opacity"
          >
            <span>Explore All Resources &amp; Search Articles</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};
